import { useEffect, useState } from "react";
import { AlertCircle, CreditCard, Clock, Info, TrendingUp, ArrowUpCircle, Wallet } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { useBillingStatus } from "@/hooks/useBillingStatus";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAutoDebitDueDay } from "@/hooks/useAutoDebitDueDay";
import { requiresPerUseCard, usePerUseCardStatus } from "@/hooks/usePerUseCardStatus";
import { autoDebitScheduledMessage, describeAutoDebitRejection } from "@/lib/auto-debit-due-day";
import { isBillingLockWaived } from "@/lib/billing-lock-waiver";
import { formatConversationCount, formatCount } from "@/lib/conversation-count";
import { effectiveConversationLimit } from "../../supabase/functions/_shared/conversation-limit";
import { daysUntil, endDetail, endHeadline } from "@/lib/subscription-end";
import { useCreditCoverage } from "@/hooks/useCreditCoverage";
import { CREDITS_TOPUP_PATH, formatCop } from "@/lib/credits";
import { isRecargasPlan } from "@/lib/credits-plan";
import { isOverWithheldCap } from "@/lib/free-quota";
import { AgencyBillingBanner } from "@/components/AgencyBillingNotice";

interface BannerOrganization {
  plan: string | null;
  conversations_used: number | null;
  conversations_limit: number | null;
  conversations_extra: number | null;
  billing_exempt: boolean | null;
  billing_no_charge: boolean | null;
  billing_override_until: string | null;
  free_quota_withheld: boolean | null;
}

export interface GracePeriodBannerProps {
  agencyManaged?: boolean;
}

export function GracePeriodBanner({ agencyManaged = false }: GracePeriodBannerProps = {}) {
  const { activeWorkspaceId } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [organizationPlanId, setOrganizationPlanId] = useState<string | null>(null);
  const [organizationLoading, setOrganizationLoading] = useState(true);
  const [usageData, setUsageData] = useState<{ used: number; limit: number } | null>(null);
  const [prepaidWaived, setPrepaidWaived] = useState(false);
  const [freeQuotaWithheld, setFreeQuotaWithheld] = useState(false);
  const [recargasOverExtras, setRecargasOverExtras] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchOrganizationPlan = async () => {
      if (!activeWorkspaceId) {
        if (mounted) { setOrganizationPlanId(null); setFreeQuotaWithheld(false); setRecargasOverExtras(false); setOrganizationLoading(false); }
        return;
      }
      const { data: row } = await supabase
        .from("organizations")
        .select("plan, conversations_used, conversations_limit, conversations_extra, billing_exempt, billing_no_charge, billing_override_until, free_quota_withheld")
        .eq("id", activeWorkspaceId)
        .maybeSingle();
      const data = row as unknown as BannerOrganization | null;
      if (mounted) {
        setOrganizationPlanId(data?.plan ?? null);
        setUsageData(data ? { used: data.conversations_used ?? 0, limit: effectiveConversationLimit(data) ?? 0 } : null);
        setPrepaidWaived(data?.billing_no_charge === true || isBillingLockWaived(data));
        setFreeQuotaWithheld(data?.free_quota_withheld === true);
        setRecargasOverExtras(!!data && isRecargasPlan(data.plan) && isOverWithheldCap(data));
        setOrganizationLoading(false);
      }
    };
    fetchOrganizationPlan();
    return () => { mounted = false; };
  }, [activeWorkspaceId]);

  const billing = useBillingStatus(activeWorkspaceId, organizationPlanId);
  const { autoDebit, loading: autoDebitLoading } = useAutoDebitDueDay({
    orgId: activeWorkspaceId,
    preapproval: billing.preapproval,
    paymentMode: billing.currentPlan?.payment_mode ?? null,
    nextBillingDate: billing.nextBillingDay,
    fallbackAmount: billing.currentPlan?.price_monthly ?? 0,
  });

  const nearQuota = !!usageData && usageData.limit > 0 && usageData.used >= usageData.limit * 0.8;
  const { coverage: creditCoverage, loading: creditCoverageLoading } = useCreditCoverage(
    activeWorkspaceId,
    brand.id === "flow" && (freeQuotaWithheld || nearQuota || recargasOverExtras),
    usageData?.used,
  );
  const creditsPay = creditCoverage?.covers === true;

  const cardRequired = brand.id === "flow" && !billing.loading && requiresPerUseCard(billing.currentPlan);
  const hasSavedCard = usePerUseCardStatus(activeWorkspaceId, cardRequired);

  if (brand.id === "etb") return null;
  if (organizationLoading || billing.loading || autoDebitLoading || creditCoverageLoading) return null;
  if (cardRequired && hasSavedCard === null) return null;

  if (agencyManaged) {
    const aiPaused = !!creditCoverage?.exhausted || (recargasOverExtras && !creditsPay) || (freeQuotaWithheld && !creditsPay);
    return aiPaused ? <AgencyBillingBanner message="La IA de este workspace está en pausa por el estado de su facturación." /> : null;
  }

  if (creditCoverage?.exhausted) {
    return (
      <div role="alert" className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive/10 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Wallet className="h-4 w-4 text-destructive shrink-0" aria-hidden="true" />
          <p className="text-destructive font-medium">
            <span className="font-bold">Saldo agotado.</span>
            {" "}Tu saldo de créditos no alcanza para una conversación más y la IA está pausada. Recarga tu saldo para que vuelva a responder.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(CREDITS_TOPUP_PATH)}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors"
        >
          Recargar saldo
        </button>
      </div>
    );
  }

  if (recargasOverExtras && !creditsPay) {
    return (
      <div role="alert" className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive/10 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Wallet className="h-4 w-4 text-destructive shrink-0" aria-hidden="true" />
          <p className="text-destructive font-medium">
            <span className="font-bold">Sin saldo.</span>
            {" "}Tu cuenta paga cada conversación con su saldo de créditos y la IA está pausada. Recarga tu saldo para que vuelva a responder.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(CREDITS_TOPUP_PATH)}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors"
        >
          Recargar saldo
        </button>
      </div>
    );
  }

  if (freeQuotaWithheld && creditsPay) {
    return (
      <div role="status" className="shadow-panel mx-3 mt-3 rounded-xl bg-blue-500/10 px-4 py-2.5 flex items-center gap-2 text-sm">
        <Wallet className="h-4 w-4 text-blue-500 shrink-0" aria-hidden="true" />
        <p className="text-blue-600 dark:text-blue-400 font-medium">
          Usando saldo de créditos: {formatCop(creditCoverage?.balanceCop ?? 0)} disponibles. Este espacio de trabajo no incluye conversaciones gratis, así que cada conversación se paga con tu saldo.
        </p>
      </div>
    );
  }

  if (freeQuotaWithheld) {
    return (
      <div role="status" className="shadow-panel mx-3 mt-3 rounded-xl bg-amber-500/15 px-4 py-2.5 flex items-center gap-2 text-sm">
        <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" aria-hidden="true" />
        <p className="text-amber-700 dark:text-amber-400 font-medium flex-1">
          Este espacio de trabajo no incluye conversaciones gratis: el plan Explora gratis de tu cuenta ya está en otro espacio de trabajo. La IA no responderá hasta que elijas un plan.
        </p>
        <button
          type="button"
          onClick={() => navigate("/app/settings/account")}
          className="shrink-0 rounded-lg bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Ver planes
        </button>
      </div>
    );
  }

  if (billing.isPrepaid && billing.expiresAt && !prepaidWaived && billing.billingState === "prepaid-expiring" && billing.daysUntilExpiry <= 7) {
    const inGrace = billing.daysUntilExpiry < 0;
    return (
      <div className="shadow-panel mx-3 mt-3 rounded-xl bg-amber-500/15 px-4 py-2.5 flex items-center gap-2 text-sm">
        <Clock className="h-4 w-4 text-amber-600 shrink-0" />
        <p className="text-amber-700 dark:text-amber-400 font-medium">
          {inGrace ? (
            <>
              <span className="font-bold">Tu plan venció el {billing.formatDate(billing.expiresAt)}.</span>
              {billing.graceDeadline ? ` Tienes acceso hasta el ${billing.formatDate(billing.graceDeadline)}.` : ""}
              {" "}Comunícate con tu asesor para renovarlo.
            </>
          ) : (
            <>
              <span className="font-bold">Tu plan vence el {billing.formatDate(billing.expiresAt)}.</span>
              {" "}Comunícate con tu asesor para renovarlo.
            </>
          )}
        </p>
      </div>
    );
  }

  // Cash payment mode: no automatic billing banners (handled manually by super admin)
  const subAny = (billing as any);
  const isCashMode = subAny?.currentPlan?.payment_mode === "cash";
  if (isCashMode) return null;

  const goToPayment = () => navigate("/app/settings/account#metodos-pago");

  if (billing.subscriptionEnd?.kind === "cancelled" || billing.subscriptionEnd?.kind === "paused") {
    return (
      <div role="status" className="shadow-panel mx-3 mt-3 rounded-xl bg-blue-500/10 px-4 py-2.5 flex items-center gap-2 text-sm">
        <Info className="h-4 w-4 text-blue-500 shrink-0" aria-hidden="true" />
        <p className="text-blue-600 dark:text-blue-400 font-medium">
          <span className="font-bold">{endHeadline(billing.subscriptionEnd)}.</span>
          {" "}{endDetail(billing.subscriptionEnd)}
        </p>
      </div>
    );
  }

  if (autoDebit?.state === "scheduled") {
    return (
      <div role="status" className="shadow-panel mx-3 mt-3 rounded-xl bg-blue-500/10 px-4 py-2.5 flex items-center gap-2 text-sm">
        <Info className="h-4 w-4 text-blue-500 shrink-0" aria-hidden="true" />
        <p className="text-blue-600 dark:text-blue-400 font-medium">
          {autoDebitScheduledMessage(autoDebit.amount)}
        </p>
      </div>
    );
  }

  const rejectionNote = autoDebit?.state === "rejected"
    ? ` Mercado Pago rechazó el cobro automático: ${describeAutoDebitRejection(autoDebit.reason)}`
    : "";

  // ── Usage approaching limit banner (for all non-custom, non-per_use plans) ──
  const usagePct = usageData && usageData.limit > 0 ? (usageData.used / usageData.limit) * 100 : 0;
  const showUsageWarning = !creditsPay && !billing.isCustomPlan && !billing.currentPlan?.billing_mode?.includes("per_use") && usagePct >= 80 && usagePct < 100 && usageData && usageData.limit > 0;

  const isPerUsePlan = billing.currentPlan?.billing_mode === "per_use";

  if (cardRequired && hasSavedCard === false) return null;

  if (isPerUsePlan && billing.hasSubscription) {
    // Per-use with payment method and billing state issues
    if (billing.billingState === "today" || billing.billingState === "overdue-grace" || billing.billingState === "overdue-expired") {
      // Fall through to custom plan billing banner logic below
    } else if (billing.billingState === "upcoming" && billing.nextBillingDate) {
      return (
        <div className="shadow-panel mx-3 mt-3 rounded-xl bg-blue-500/10 px-4 py-2 flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <Info className="h-4 w-4 text-blue-500 shrink-0" />
            <p className="text-blue-600 dark:text-blue-400 font-medium">
              Tu próxima fecha de pago es el <span className="font-bold">{billing.formatDate(billing.nextBillingDate)}</span>.
            </p>
          </div>
          <button onClick={goToPayment} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors">
            <CreditCard className="h-3.5 w-3.5" /> Ir a pagar
          </button>
        </div>
      );
    } else {
      return null;
    }
  }

  // Non-custom, non-per_use plans: legacy grace period logic only
  if (!billing.isCustomPlan && !isPerUsePlan) {
    if (billing.subscriptionEnd?.kind === "payment_failed") {
      const daysLeft = daysUntil(billing.subscriptionEnd.suspendOn);
      return (
        <div className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive/10 px-4 py-2 flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
            <p className="text-destructive font-medium truncate">
              Pago pendiente — tu cuenta se suspenderá en {daysLeft} día{daysLeft !== 1 ? "s" : ""}.
            </p>
          </div>
          <a
            href="https://www.mercadopago.com.co/subscriptions"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors"
          >
            <CreditCard className="h-3.5 w-3.5" /> Actualizar pago
          </a>
        </div>
      );
    }

    // Show usage warning banner when approaching limit (80-99%)
    if (showUsageWarning && usageData) {
      return (
        <div className="shadow-panel mx-3 mt-3 rounded-xl bg-amber-500/15 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <TrendingUp className="h-4 w-4 text-amber-600 shrink-0" />
            <p className="text-amber-700 dark:text-amber-400 font-medium">
              <span className="font-bold">Estás cerca del límite:</span>
              {" "}{formatCount(usageData.used)}/{formatConversationCount(usageData.limit)} conversaciones usadas ({Math.round(usagePct)}%).
              {" "}Contrata un plan para no perder el servicio de IA.
            </p>
          </div>
          <button
            onClick={goToPayment}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition-colors"
          >
            <ArrowUpCircle className="h-3.5 w-3.5" /> Ver planes
          </button>
        </div>
      );
    }

    return null;
  }

  // ── Custom plan billing banner ──
  const { billingState, missingPaymentMethod, nextBillingDate, graceDeadline, daysOverdue, currentPlan, formatDate, isPrepaid, expiresAt, daysUntilExpiry, hasSubscription } = billing;

  // ── Prepaid plans: no banner at all unless expired ──
  if (isPrepaid && billingState === "prepaid-expired" && expiresAt) {
    return (
      <div className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive/10 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="text-destructive font-medium">
            <span className="font-bold">Tu plan prepagado venció el {formatDate(expiresAt)}.</span>
            {" "}Contacta a tu asesor para renovar o activar un nuevo plan.
          </p>
        </div>
        <button onClick={goToPayment} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors">
          <CreditCard className="h-3.5 w-3.5" /> Ver cuenta
        </button>
      </div>
    );
  }

  // Prepaid plan that hasn't expired — no banner needed
  if (isPrepaid) return null;

  // No billing date and no payment method only for active billable subscriptions
  if (hasSubscription && billingState === "none" && missingPaymentMethod && !isPrepaid) {
    return (
      <div className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive/10 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="text-destructive font-medium">
            <span className="font-bold">Método de pago requerido</span>
            {" — "}
            Tienes un plan personalizado ({currentPlan?.name}). Necesitas agregar un método de pago para continuar.
          </p>
        </div>
        <button onClick={goToPayment} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors">
          <CreditCard className="h-3.5 w-3.5" /> Agregar método de pago
        </button>
      </div>
    );
  }

  // No billing state at all
  if (billingState === "none") return null;

  // Payment method warning suffix
  const pmWarning = missingPaymentMethod ? " (Sin método de pago registrado)" : "";

  if (billingState === "today") {
    return (
      <div className="shadow-panel mx-3 mt-3 rounded-xl bg-amber-500/15 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-amber-700 dark:text-amber-400 font-medium">
            <span className="font-bold">Hoy es tu fecha de pago.</span>
            {" "}Plan: {currentPlan?.name}. Tienes 5 días para regularizar tu pago.
            {rejectionNote}
            {pmWarning && <span className="text-destructive font-semibold">{pmWarning}</span>}
          </p>
        </div>
        <button onClick={goToPayment} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition-colors">
          <CreditCard className="h-3.5 w-3.5" /> Ir a pagos
        </button>
      </div>
    );
  }

  if (billingState === "overdue-grace" && nextBillingDate && graceDeadline) {
    return (
      <div className="shadow-panel mx-3 mt-3 rounded-xl bg-orange-500/15 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="h-4 w-4 text-orange-600 shrink-0" />
          <p className="text-orange-700 dark:text-orange-400 font-medium">
            <span className="font-bold">Tu pago tiene {daysOverdue} día{daysOverdue !== 1 ? "s" : ""} de retraso.</span>
            {" "}Regulariza a más tardar el <span className="font-bold">{formatDate(graceDeadline)}</span> para evitar la suspensión.
            {rejectionNote}
            {pmWarning && <span className="text-destructive font-semibold">{pmWarning}</span>}
          </p>
        </div>
        <button onClick={goToPayment} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-orange-600 text-white text-xs font-medium hover:bg-orange-700 transition-colors">
          <CreditCard className="h-3.5 w-3.5" /> Regularizar pago
        </button>
      </div>
    );
  }

  if (billingState === "overdue-expired" && nextBillingDate) {
    return (
      <div className="shadow-panel mx-3 mt-3 rounded-xl bg-destructive/10 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="text-destructive font-medium">
            <span className="font-bold">Pago vencido hace {daysOverdue} días.</span>
            {" "}Tu cuenta puede ser suspendida en cualquier momento. Regulariza tu pago inmediatamente.
            {rejectionNote}
            {pmWarning && <span className="font-semibold">{pmWarning}</span>}
          </p>
        </div>
        <button onClick={goToPayment} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors">
          <CreditCard className="h-3.5 w-3.5" /> Pagar ahora
        </button>
      </div>
    );
  }

  // Non-per_use upcoming: no banner here. The Suscripción section in
  // AccountTab owns the "Authorize subscription" CTA for preapproval plans,
  // so a separate banner would just create a second, competing call to action.
  return null;
}
